
import { useQuery } from 'react-query';
import { Link } from 'react-router-dom';
import { dashboardApi } from '../services/api';
import { useAuth } from '../hooks/useAuth';
import ComingSoon from '../components/ComingSoon';
import { 
  Video, 
  HardDrive, 
  Download, 
  Upload as UploadIcon, 
  TrendingUp,
  Clock,
  Crown,
  RefreshCw,
  ArrowRight
} from 'lucide-react';

export default function Dashboard() {
  const { user } = useAuth();

  const { data: stats, isLoading: statsLoading, refetch: refetchStats } = useQuery(
    'dashboard-stats',
    () => dashboardApi.getStats().then(res => res.data.data)
  );

  const { data: activity, isLoading: activityLoading } = useQuery(
    'dashboard-activity',
    () => dashboardApi.getActivity(5).then(res => res.data.data)
  );

  const { data: storage, isLoading: storageLoading } = useQuery(
    'dashboard-storage',
    () => dashboardApi.getStorageBreakdown().then(res => res.data.data)
  );

  const handleRefreshStats = async () => {
    await refetchStats();
  };

  if (statsLoading) {
    return (
      <div className="animate-pulse space-y-6">
        <div className="h-8 bg-gray-200 rounded w-1/4"></div>
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="bg-white p-6 rounded-lg shadow h-32"></div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            Welcome back, {user?.displayName}!
          </h1>
          <p className="mt-1 text-sm text-gray-500">
            Here's what's happening with your VOD archive
          </p>
        </div>
        <div className="flex items-center space-x-3">
          {user?.role === 'premium' && (
            <div className="flex items-center px-3 py-1 bg-yellow-100 text-yellow-800 rounded-full text-sm font-medium relative">
              <Crown className="h-4 w-4 mr-1" />
              Premium
              <ComingSoon size="sm" position="top-right" className="absolute -top-1 -right-1" />
            </div>
          )}
          <button
            onClick={handleRefreshStats}
            className="btn-secondary"
            disabled={statsLoading}
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${statsLoading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
        <div className="card">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <Video className="h-8 w-8 text-twitch-600" />
            </div>
            <div className="ml-5 w-0 flex-1">
              <dl>
                <dt className="text-sm font-medium text-gray-500 truncate">
                  Total Videos
                </dt>
                <dd className="text-lg font-medium text-gray-900">
                  {stats?.totalVideos || 0}
                </dd>
              </dl>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <HardDrive className="h-8 w-8 text-blue-600" />
            </div>
            <div className="ml-5 w-0 flex-1">
              <dl>
                <dt className="text-sm font-medium text-gray-500 truncate">
                  Storage Used
                </dt>
                <dd className="text-lg font-medium text-gray-900">
                  {stats?.totalStorageUsedFormatted || '0 Bytes'}
                </dd>
              </dl>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <Download className="h-8 w-8 text-green-600" />
            </div>
            <div className="ml-5 w-0 flex-1">
              <dl>
                <dt className="text-sm font-medium text-gray-500 truncate">
                  VODs Downloaded
                </dt>
                <dd className="text-lg font-medium text-gray-900">
                  {stats?.totalVodsDownloaded || 0}
                </dd>
              </dl>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <Clock className="h-8 w-8 text-purple-600" />
            </div>
            <div className="ml-5 w-0 flex-1">
              <dl>
                <dt className="text-sm font-medium text-gray-500 truncate">
                  Retention Period
                </dt>
                <dd className="text-lg font-medium text-gray-900">
                  {stats?.retentionPeriodDays || 365} days
                </dd>
              </dl>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Activity */}
        <div className="card">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-gray-900">Recent Activity</h3>
            <Link
              to="/vods"
              className="text-sm text-twitch-600 hover:text-twitch-700 flex items-center"
            >
              View all
              <ArrowRight className="h-4 w-4 ml-1" />
            </Link>
          </div>
          
          {activityLoading ? (
            <div className="space-y-3">
              {[...Array(3)].map((_, i) => (
                <div key={i} className="animate-pulse flex space-x-3">
                  <div className="h-10 w-10 bg-gray-200 rounded"></div>
                  <div className="flex-1 space-y-2">
                    <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                    <div className="h-3 bg-gray-200 rounded w-1/2"></div>
                  </div>
                </div>
              ))}
            </div>
          ) : activity?.recentVods.length ? (
            <div className="space-y-3">
              {activity.recentVods.map((vod) => (
                <div key={vod.id} className="flex items-center space-x-3">
                  <div className={`flex-shrink-0 h-10 w-10 rounded-lg flex items-center justify-center ${
                    vod.source === 'twitch' ? 'bg-twitch-100' : 'bg-blue-100'
                  }`}>
                    {vod.source === 'twitch' ? (
                      <Download className="h-5 w-5 text-twitch-600" />
                    ) : (
                      <UploadIcon className="h-5 w-5 text-blue-600" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">
                      {vod.title}
                    </p>
                    <p className="text-xs text-gray-500">
                      {vod.fileSizeFormatted} • {new Date(vod.uploadedAt).toLocaleDateString()}
                    </p>
                  </div>
                  <div className={`flex-shrink-0 px-2 py-1 text-xs rounded-full ${
                    vod.status === 'ready' ? 'bg-green-100 text-green-800' :
                    vod.status === 'processing' ? 'bg-yellow-100 text-yellow-800' :
                    vod.status === 'error' ? 'bg-red-100 text-red-800' :
                    'bg-gray-100 text-gray-800'
                  }`}>
                    {vod.status}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-6">
              <Video className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">No VODs yet</h3>
              <p className="mt-1 text-sm text-gray-500">
                Start by uploading a VOD or syncing from Twitch.
              </p>
              <div className="mt-6 flex justify-center space-x-3">
                <Link to="/upload" className="btn-primary">
                  <UploadIcon className="h-4 w-4 mr-2" />
                  Upload VOD
                </Link>
                <Link to="/twitch" className="btn-secondary">
                  <Download className="h-4 w-4 mr-2" />
                  Sync from Twitch
                </Link>
              </div>
            </div>
          )}
        </div>

        {/* Storage Breakdown */}
        <div className="card">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-gray-900">Storage Breakdown</h3>
            <Link
              to="/analytics"
              className="text-sm text-twitch-600 hover:text-twitch-700 flex items-center"
            >
              View analytics
              <ArrowRight className="h-4 w-4 ml-1" />
            </Link>
          </div>
          
          {storageLoading ? (
            <div className="space-y-3">
              {[...Array(3)].map((_, i) => (
                <div key={i} className="animate-pulse">
                  <div className="h-4 bg-gray-200 rounded w-1/3 mb-2"></div>
                  <div className="h-2 bg-gray-200 rounded"></div>
                </div>
              ))}
            </div>
          ) : storage?.bySource.length ? (
            <div className="space-y-4">
              {storage.bySource.map((item) => (
                <div key={item.source}>
                  <div className="flex justify-between text-sm">
                    <span className="font-medium text-gray-900 capitalize">
                      {item.source} ({item.count} files)
                    </span>
                    <span className="text-gray-500">{item.sizeFormatted}</span>
                  </div>
                  <div className="mt-1 progress-bar">
                    <div
                      className="progress-fill"
                      style={{
                        width: `${stats?.totalStorageUsed ? (item.size / stats.totalStorageUsed) * 100 : 0}%`
                      }}
                    ></div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-6">
              <HardDrive className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">No storage used</h3>
              <p className="mt-1 text-sm text-gray-500">
                Upload some VODs to see your storage breakdown.
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Quick Actions */}
      <div className="card">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Quick Actions</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <Link
            to="/upload"
            className="flex items-center p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <UploadIcon className="h-8 w-8 text-blue-600 mr-3" />
            <div>
              <p className="font-medium text-gray-900">Upload VOD</p>
              <p className="text-sm text-gray-500">Upload a local video file</p>
            </div>
          </Link>
          
          <Link
            to="/twitch"
            className="flex items-center p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <Download className="h-8 w-8 text-twitch-600 mr-3" />
            <div>
              <p className="font-medium text-gray-900">Sync Twitch</p>
              <p className="text-sm text-gray-500">Backup from Twitch</p>
            </div>
          </Link>
          
          <Link
            to="/vods"
            className="flex items-center p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <Video className="h-8 w-8 text-green-600 mr-3" />
            <div>
              <p className="font-medium text-gray-900">Browse VODs</p>
              <p className="text-sm text-gray-500">View your archive</p>
            </div>
          </Link>
          
          <Link
            to="/analytics"
            className="flex items-center p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <TrendingUp className="h-8 w-8 text-purple-600 mr-3" />
            <div>
              <p className="font-medium text-gray-900">Analytics</p>
              <p className="text-sm text-gray-500">View detailed stats</p>
            </div>
          </Link>
        </div>
      </div>
    </div>
  );
}